<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import ZoneGrid from './ZoneGrid.vue'

const props = defineProps<{
  exchange: string
  apiBase: string
}>()

const apiBase = (props.apiBase || '/api/desk').replace(/\/$/, '')

const status = ref<any>(null)
const cycles = ref<any[]>([])
const captures = ref<any[]>([])
const frame = ref<any>(null)

const selectedCycleId = ref('')
const symbol = ref('BTCUSDT')
const timeframe = ref('24h')

const showLevels = ref(true)
const focus = ref<{ price: number; at: number } | null>(null)

const loadingCycles = ref(false)
const loadingFrame = ref(false)
const errorMessage = ref('')

const available = computed(() => status.value?.available === true)

const symbolOptions = computed(() => {
  const fromCycle = cycles.value.find((c) => c.cycle_id === selectedCycleId.value)
  const list = fromCycle?.symbols?.length
    ? fromCycle.symbols
    : [...new Set(cycles.value.flatMap((c) => c.symbols || []))]
  return (list.length ? list : ['BTCUSDT']).map((s: string) => ({
    value: s,
    label: s.endsWith('USDT') ? s.slice(0, -4) : s,
  }))
})

const timeframeOptions = computed(() => {
  const fromCycle = cycles.value.find((c) => c.cycle_id === selectedCycleId.value)
  const caps = captures.value.filter((c) => c.symbol === symbol.value)
  if (caps.length) {
    return caps.map((c) => c.timeframe)
  }
  return fromCycle?.timeframes?.length ? fromCycle.timeframes : ['48h', '24h', '12h']
})

/** Strongest liquidity peaks in the latest column (the yellow profile bars). */
const topLevels = computed(() => {
  const f = frame.value
  if (!f?.matrix?.length || !f?.prices?.length) return []
  const col: number[] = (f.matrix[f.matrix.length - 1] || []).map((v: any) => Number(v) || 0)
  const peak = Math.max(1e-9, ...col)
  const lastClose = f.candles?.length ? Number(f.candles[f.candles.length - 1].c) : 0
  const ref = Number(f.reference_price || lastClose || 0)
  const peaks: Array<{ price: number; size: number; pct: number; above: boolean; dist: number }> = []
  for (let j = 0; j < f.prices.length; j++) {
    const v = col[j]
    if (v <= 0 || v / peak < 0.2) continue
    if (v < (col[j - 1] || 0) || v < (col[j + 1] || 0)) continue
    const price = Number(f.prices[j])
    peaks.push({
      price,
      size: v,
      pct: (v / peak) * 100,
      above: ref ? price >= ref : false,
      dist: ref ? (price / ref - 1) * 100 : 0,
    })
  }
  return peaks.sort((a, b) => b.size - a.size).slice(0, 10).sort((a, b) => b.price - a.price)
})

function focusLevel(price: number) {
  focus.value = { price, at: Date.now() }
}

function formatLevelPrice(p: number): string {
  if (p >= 1000) return p.toLocaleString('en-US', { maximumFractionDigits: 1 })
  if (p >= 1) return p.toLocaleString('en-US', { maximumFractionDigits: 4 })
  return p.toLocaleString('en-US', { maximumFractionDigits: 6 })
}

function formatLevelSize(v: number): string {
  if (v >= 1e6) return `${(v / 1e6).toFixed(2)}M`
  if (v >= 1e3) return `${(v / 1e3).toFixed(1)}K`
  return v.toFixed(0)
}

function formatWhen(unixOrIso: any): string {
  if (!unixOrIso) return '—'
  const sec = typeof unixOrIso === 'number'
    ? (unixOrIso > 1e12 ? Math.floor(unixOrIso / 1000) : unixOrIso)
    : Math.floor(new Date(unixOrIso).getTime() / 1000)
  if (!sec) return '—'
  return new Date(sec * 1000).toLocaleString('fa-IR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
}

async function fetchJson(url: string) {
  const res = await fetch(url, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  })
  const data = await res.json()
  if (!res.ok && data?.error) {
    throw new Error(data.error)
  }
  return data
}

async function loadStatus() {
  status.value = await fetchJson(`${apiBase}/history/status`)
}

async function loadCycles() {
  loadingCycles.value = true
  errorMessage.value = ''
  try {
    const data = await fetchJson(`${apiBase}/history/cycles?limit=100`)
    cycles.value = data.cycles || []
    if (!selectedCycleId.value && cycles.value.length) {
      selectedCycleId.value = cycles.value[0].cycle_id
    }
  } catch (e: any) {
    errorMessage.value = e?.message || String(e)
  } finally {
    loadingCycles.value = false
  }
}

async function loadCaptures() {
  if (!selectedCycleId.value) {
    captures.value = []
    return
  }
  const data = await fetchJson(
    `${apiBase}/history/cycles/${encodeURIComponent(selectedCycleId.value)}/captures`,
  )
  captures.value = data.captures || []

  const syms = [...new Set(captures.value.map((c: any) => c.symbol))]
  if (syms.length && !syms.includes(symbol.value)) {
    symbol.value = syms[0]
  }
  const tfs = captures.value
    .filter((c: any) => c.symbol === symbol.value)
    .map((c: any) => c.timeframe)
  if (tfs.length && !tfs.includes(timeframe.value)) {
    timeframe.value = tfs[0]
  }
}

async function loadFrame() {
  if (!selectedCycleId.value || !symbol.value || !timeframe.value) {
    frame.value = null
    return
  }
  loadingFrame.value = true
  errorMessage.value = ''
  try {
    const qs = new URLSearchParams({
      cycle_id: selectedCycleId.value,
      symbol: symbol.value,
      timeframe: timeframe.value,
    })
    const data = await fetchJson(`${apiBase}/history/frame?${qs}`)
    frame.value = data.frame || null
  } catch (e: any) {
    frame.value = null
    errorMessage.value = e?.message || String(e)
  } finally {
    loadingFrame.value = false
  }
}

watch(selectedCycleId, async () => {
  await loadCaptures()
  await loadFrame()
})

watch([symbol, timeframe], async () => {
  await loadFrame()
})

onMounted(async () => {
  try {
    await loadStatus()
    await loadCycles()
    await loadCaptures()
    await loadFrame()
  } catch (e: any) {
    errorMessage.value = e?.message || String(e)
  }
})
</script>

<template>
  <div class="space-y-4" dir="ltr">
    <div class="panel-glass rounded-[1.75rem] p-4 sm:rounded-[2rem] sm:p-5">
      <div class="panel-glass-content flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p class="text-sm text-zinc-400">
            {{ __('Capture history') }}
            · {{ __('latest cycle only') }}
          </p>
          <p v-if="status" class="mt-1 text-xs text-zinc-500">
            <span v-if="available">
              {{ status.documents ?? 0 }} {{ __('documents') }}
              <template v-if="status.newest_captured_at">
                · {{ __('newest') }} {{ formatWhen(status.newest_captured_at) }}
              </template>
            </span>
            <span v-else class="text-amber-400">
              {{ status.reason || __('Capture history unavailable') }}
            </span>
          </p>
        </div>
        <button
          type="button"
          class="panel-glass-btn rounded-full px-4 py-2 text-sm font-medium"
          :disabled="loadingCycles"
          @click="loadCycles"
        >
          {{ loadingCycles ? __('Loading…') : __('Refresh cycles') }}
        </button>
      </div>
    </div>

    <div
      v-if="errorMessage"
      class="rounded-2xl border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-200"
    >
      {{ errorMessage }}
    </div>

    <div class="grid gap-4 xl:grid-cols-[22rem_minmax(0,1fr)]">
      <div class="panel-glass max-h-[70vh] overflow-auto rounded-[1.75rem] p-3 sm:rounded-[2rem]">
        <div class="panel-glass-content space-y-2">
          <p class="px-2 text-[10px] font-bold uppercase tracking-wider text-zinc-400">
            {{ __('Cycles') }}
          </p>
          <button
            v-for="c in cycles"
            :key="c.cycle_id"
            type="button"
            class="w-full rounded-2xl px-3 py-3 text-left transition"
            :class="
              selectedCycleId === c.cycle_id
                ? 'bg-white/10 ring-1 ring-white/20'
                : 'hover:bg-white/5'
            "
            @click="selectedCycleId = c.cycle_id"
          >
            <div class="text-sm font-medium text-zinc-100">
              {{ formatWhen(c.captured_unix || c.captured_at) }}
            </div>
            <div class="mt-1 text-xs text-zinc-400">
              {{ c.captures }} {{ __('captures') }}
              · {{ (c.symbols || []).slice(0, 4).join(', ') }}
              <span v-if="(c.symbols || []).length > 4">…</span>
            </div>
            <div class="mt-1 font-mono text-[10px] text-zinc-500">
              {{ String(c.cycle_id).slice(0, 12) }}…
            </div>
          </button>
          <p v-if="!cycles.length && !loadingCycles" class="px-2 py-6 text-sm text-zinc-500">
            {{ __('No cycles stored yet. Wait for the next collector round.') }}
          </p>
        </div>
      </div>

      <div class="space-y-4">
        <div class="panel-glass rounded-[1.75rem] p-4 sm:rounded-[2rem]">
          <div class="panel-glass-content flex flex-col gap-4 lg:flex-row lg:flex-wrap lg:items-end">
            <div class="min-w-[10rem] space-y-2">
              <label class="block text-[10px] font-bold uppercase tracking-wider text-zinc-400">
                Pair
              </label>
              <SelectControl
                :selected="symbol"
                :options="symbolOptions"
                size="md"
                @change="symbol = $event"
              />
            </div>
            <div class="space-y-2">
              <label class="block text-[10px] font-bold uppercase tracking-wider text-zinc-400">
                Timeframe
              </label>
              <div class="flex flex-wrap gap-2">
                <button
                  v-for="tf in timeframeOptions"
                  :key="tf"
                  type="button"
                  class="panel-glass-btn rounded-full px-3.5 py-2 text-sm font-medium"
                  :class="{ 'panel-glass-btn--active': timeframe === tf }"
                  @click="timeframe = tf"
                >
                  {{ tf }}
                </button>
              </div>
            </div>
            <p class="text-xs text-zinc-500 lg:ml-auto">
              <span v-if="loadingFrame">{{ __('Loading zones…') }}</span>
              <span v-else-if="frame">
                {{ frame.cells }} {{ __('cells') }}
                · max {{ Number(frame.max_size || 0).toLocaleString() }}
              </span>
            </p>
          </div>
        </div>

        <div class="panel-glass overflow-hidden rounded-[1.75rem] sm:rounded-[2rem]">
          <div class="panel-glass-content p-2 sm:p-3">
            <ZoneGrid
              v-if="frame"
              :frame="frame"
              :levels="topLevels"
              :show-levels="showLevels"
              :focus="focus"
              class="h-[62vh] min-h-[420px] w-full"
            />
            <div
              v-else
              class="flex h-[62vh] min-h-[420px] items-center justify-center text-sm text-zinc-500"
            >
              {{ available ? __('Select a cycle to view its zones.') : __('History store offline.') }}
            </div>
          </div>
        </div>

        <div v-if="frame && topLevels.length" class="panel-glass rounded-[1.75rem] p-4 sm:rounded-[2rem]">
          <div class="panel-glass-content space-y-3">
            <div class="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p class="text-sm font-semibold text-zinc-100">{{ __('Strongest levels (latest capture)') }}</p>
                <p class="text-xs text-zinc-500">{{ __('Click a level to jump to it on the chart.') }}</p>
              </div>
              <label class="flex cursor-pointer items-center gap-2 text-xs text-zinc-300">
                <input v-model="showLevels" type="checkbox" class="rounded border-zinc-600 bg-zinc-900">
                {{ __('Show level prices on chart') }}
              </label>
            </div>
            <div class="grid gap-2 sm:grid-cols-2">
              <button
                v-for="lv in topLevels"
                :key="lv.price"
                type="button"
                class="flex items-center gap-3 rounded-xl px-3 py-2 text-left ring-1 ring-white/10 transition hover:bg-white/5"
                @click="focusLevel(lv.price)"
              >
                <span class="w-28 shrink-0 font-mono text-sm font-semibold text-white">{{ formatLevelPrice(lv.price) }}</span>
                <span class="h-1.5 flex-1 overflow-hidden rounded-full bg-zinc-800">
                  <span class="block h-full rounded-full" :class="lv.pct >= 80 ? 'bg-yellow-400' : lv.pct >= 55 ? 'bg-cyan-400' : 'bg-violet-400'" :style="{ width: `${lv.pct}%` }" />
                </span>
                <span class="w-14 shrink-0 text-right font-mono text-xs text-zinc-300">{{ formatLevelSize(lv.size) }}</span>
                <span class="w-16 shrink-0 text-right font-mono text-xs" :class="lv.above ? 'text-amber-300' : 'text-rose-300'">{{ lv.dist >= 0 ? '+' : '' }}{{ lv.dist.toFixed(2) }}%</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
